<script setup>
import { ref, computed } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import PlatformTooltipIcon from 'dashboard/components-next/PlatformTooltipIcon.vue';

const emit = defineEmits(['import']);

const store = useStore();
const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);
const contactAttributes = useMapGetter('attributes/getContactAttributes');

// State
const step = ref('upload'); // 'upload', 'mapping', 'importing'
const file = ref(null);
const fileInput = ref(null);
const csvColumns = ref([]);
const previewRows = ref([]);
const totalRows = ref(0);
const loading = ref(false);
const error = ref(null);
const columnMappings = ref({});
const isOpen = ref(false);

// Mappable system fields for contacts
const mappableFields = [
  { value: '', label: '-- Ignorar --' },
  { value: 'name', label: 'Nome', required: true },
  { value: 'email', label: 'E-mail' },
  { value: 'phone_number', label: 'Telefone' },

  { value: 'company', label: 'Empresa' },
  { value: 'city', label: 'Cidade' },
  { value: 'label', label: 'Etiqueta' },
];

// Computed: merges system fields with dynamic custom attributes from the store
const allMappableFields = computed(() => {
  const attrs = (contactAttributes.value || []).map(attr => ({
    value: attr.attributeKey,
    label: `📋 ${attr.attributeDisplayName}`,
    isCustom: true,
  }));
  if (attrs.length === 0) return mappableFields;
  return [
    ...mappableFields,
    { value: '', label: '── Atributos ──', disabled: true },
    ...attrs,
  ];
});

// Check if the required 'name' field is mapped
const isMappingValid = computed(() => {
  return Object.values(columnMappings.value).includes('name');
});

// Returns available fields for a given column, filtering out already-used fields
const getAvailableFields = currentColumn => {
  const usedFields = new Set(
    Object.entries(columnMappings.value)
      .filter(([col, val]) => col !== currentColumn && val !== '')
      .map(([, val]) => val)
  );
  return allMappableFields.value.filter(
    f =>
      f.value === '' ||
      f.value === columnMappings.value[currentColumn] ||
      !usedFields.has(f.value)
  );
};

const getFieldLabel = value => {
  const field = allMappableFields.value.find(f => f.value === value);
  return field ? field.label : value;
};

// Auto-detect mappings based on column header names
const autoDetectMappings = columns => {
  const detectionMap = {
    name: [
      'name',
      'nome',
      'full_name',
      'nome_completo',
      'contact_name',
      'nome_contato',
      'first_name',
      'primeiro_nome',
    ],
    email: [
      'email',
      'e_mail',
      'e-mail',
      'email_address',
      'endereco_email',
      'correo',
      'mail',
    ],
    phone_number: [
      'phone',
      'phone_number',
      'telefone',
      'tel',
      'celular',
      'mobile',
      'whatsapp',
      'numero',
      'fone',
      'numero_telefone',
    ],

    company: [
      'company',
      'empresa',
      'company_name',
      'nome_empresa',
      'organizacao',
      'organization',
    ],
    city: ['city', 'cidade', 'municipio', 'localidade'],
    label: [
      'label',
      'labels',
      'tag',
      'tags',
      'etiqueta',
      'etiquetas',
      'categoria',
    ],
  };

  const mappings = {};
  columns.forEach(column => {
    const normalizedCol = column
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_]/g, '_');

    // 1. Try system fields first
    const match = Object.entries(detectionMap).find(
      ([systemField, patterns]) =>
        patterns.includes(normalizedCol) &&
        !Object.values(mappings).includes(systemField)
    );

    if (match) {
      mappings[column] = match[0];
      return;
    }

    // 2. Try matching against custom attribute keys and display names
    const attrs = contactAttributes.value || [];
    const attrMatch = attrs.find(attr => {
      const key = (attr.attributeKey || '').toLowerCase();
      const display = (attr.attributeDisplayName || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9_]/g, '_');
      return (
        (normalizedCol === key || normalizedCol === display) &&
        !Object.values(mappings).includes(attr.attributeKey)
      );
    });

    mappings[column] = attrMatch ? attrMatch.attributeKey : '';
  });

  return mappings;
};

const handleFileSelect = async event => {
  const selectedFile = event.target?.files?.[0];
  if (!selectedFile) return;

  const fileName = selectedFile.name.toLowerCase();
  const validExtensions = ['.csv', '.xlsx', '.xls'];
  const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));

  if (!isValidFile) {
    error.value = 'Por favor, selecione um arquivo CSV, XLSX ou XLS';
    return;
  }

  file.value = selectedFile;
  error.value = null;
  loading.value = true;

  try {
    const response = await ContactAPI.importPreview(selectedFile);
    csvColumns.value = response.data.columns || [];
    previewRows.value = response.data.preview_rows || [];
    totalRows.value = response.data.total_rows || 0;

    // Auto-detect column mappings
    columnMappings.value = autoDetectMappings(csvColumns.value);

    step.value = 'mapping';
  } catch (err) {
    error.value = err.response?.data?.error || 'Erro ao processar arquivo';
  } finally {
    loading.value = false;
  }
};

const handleFileDrop = event => {
  event.preventDefault();
  const droppedFile = event.dataTransfer?.files?.[0];
  if (!droppedFile) return;

  const fileName = droppedFile.name.toLowerCase();
  const validExtensions = ['.csv', '.xlsx', '.xls'];
  const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));

  if (isValidFile) {
    const mockEvent = { target: { files: [droppedFile] } };
    handleFileSelect(mockEvent);
  } else {
    error.value = 'Por favor, arraste um arquivo CSV, XLSX ou XLS';
  }
};

const handleDragOver = event => {
  event.preventDefault();
};

const handleImport = () => {
  if (!isMappingValid.value) return;

  // Build clean mappings object (only mapped columns)
  const cleanMappings = {};
  Object.entries(columnMappings.value).forEach(([csvCol, systemField]) => {
    if (systemField && systemField !== '__separator__') {
      cleanMappings[csvCol] = systemField;
    }
  });

  step.value = 'importing';
  emit('import', { file: file.value, mappings: cleanMappings });
};

const handleReset = () => {
  step.value = 'upload';
  file.value = null;
  csvColumns.value = [];
  previewRows.value = [];
  totalRows.value = 0;
  columnMappings.value = {};
  error.value = null;
  if (fileInput.value) {
    fileInput.value.value = null;
  }
};

const open = () => {
  isOpen.value = true;
  handleReset();
  // Load fresh custom attributes for the mapping dropdown
  store.dispatch('attributes/get');
};

const close = () => {
  isOpen.value = false;
  handleReset();
};

const handleClose = () => {
  if (step.value !== 'importing') {
    close();
  }
};

// Expose for parent component (same pattern as Dialog)
defineExpose({
  dialogRef: {
    open,
    close,
  },
});
</script>

<template>
  <teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-[4px]"
        @click="handleClose"
      />

      <!-- Modal -->
      <div
        class="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-xl shadow-xl mx-4 dark:border dark:border-[#2A2B33]"
      >
        <div class="p-6">
          <!-- Header -->
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <h3
                class="text-lg font-medium text-slate-900 dark:text-slate-100"
              >
                {{ $t('IMPORT_CONTACTS.TITLE', 'Importar Contatos') }}
              </h3>
              <PlatformTooltipIcon identifier="contacts_import" />

              <!-- Step indicator -->
              <div class="flex items-center gap-1 ml-2">
                <span
                  class="w-2 h-2 rounded-full"
                  :class="
                    step === 'upload'
                      ? 'bg-woot-500'
                      : 'bg-slate-300 dark:bg-slate-600'
                  "
                />
                <span
                  class="w-2 h-2 rounded-full"
                  :class="
                    step === 'mapping'
                      ? 'bg-woot-500'
                      : 'bg-slate-300 dark:bg-slate-600'
                  "
                />
                <span
                  class="w-2 h-2 rounded-full"
                  :class="
                    step === 'importing'
                      ? 'bg-woot-500'
                      : 'bg-slate-300 dark:bg-slate-600'
                  "
                />
              </div>
            </div>
            <button
              v-if="step !== 'importing'"
              class="px-3 py-1 text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
              @click="handleClose"
            >
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              {{ $t('IMPORT_CONTACTS.CLOSE', 'Fechar') }}
            </button>
          </div>

          <!-- Error Message -->
          <div
            v-if="error"
            class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm"
          >
            {{ error }}
          </div>

          <!-- Step 1: Upload -->
          <div v-if="step === 'upload'">
            <div
              class="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-woot-500 dark:hover:border-woot-500 transition-colors"
              @drop="handleFileDrop"
              @dragover="handleDragOver"
              @click="fileInput?.click()"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                class="hidden"
                @change="handleFileSelect"
              />
              <div class="flex justify-center mb-4 text-slate-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"
                  />
                  <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                  <path d="M10 9H8" />
                  <path d="M16 13H8" />
                  <path d="M16 17H8" />
                </svg>
              </div>
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <p class="text-sm text-slate-600 dark:text-slate-400 mb-2">
                {{
                  $t(
                    'IMPORT_CONTACTS.DROP_AREA',
                    'Clique para selecionar ou arraste um arquivo aqui'
                  )
                }}
              </p>
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <p class="text-xs text-slate-500 dark:text-slate-500">
                {{
                  $t(
                    'IMPORT_CONTACTS.FORMATS',
                    'Formatos aceitos: .csv, .xlsx, .xls'
                  )
                }}
              </p>
            </div>

            <div
              v-if="loading"
              class="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500"
            >
              <svg
                class="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              {{
                $t('IMPORT_CONTACTS.PROCESSING', 'Processando arquivo...')
              }}
            </div>
          </div>

          <!-- Step 2: Mapping -->
          <div v-if="step === 'mapping'">
            <div class="mb-4">
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <p class="text-sm text-slate-600 dark:text-slate-400 mb-1">
                {{ $t('IMPORT_CONTACTS.FILE_LABEL', 'Arquivo:') }}
                <strong>{{ file?.name }}</strong>
                ({{
                  totalRows
                }}
                {{ $t('IMPORT_CONTACTS.ROWS', 'linhas') }})
              </p>
              <div class="flex items-center gap-2 mb-4">
                <p class="text-sm text-slate-600 dark:text-slate-400">
                  {{
                    $t(
                      'IMPORT_CONTACTS.MAP_COLUMNS',
                      'Mapeie as colunas do arquivo para os campos do sistema:'
                    )
                  }}
                </p>
                <PlatformTooltipIcon
                  identifier="contacts_import_mapping"
                  size="sm"
                />
              </div>
              <!-- eslint-enable vue/no-bare-strings-in-template -->
            </div>

            <!-- Preview Table -->
            <div
              class="mb-6 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden max-h-40 overflow-y-auto"
            >
              <table class="w-full text-xs">
                <thead class="bg-slate-50 dark:bg-slate-800 sticky top-0">
                  <tr>
                    <th
                      v-for="column in csvColumns"
                      :key="column"
                      class="px-3 py-2 text-left font-medium text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 whitespace-nowrap"
                    >
                      {{ column }}
                    </th>
                  </tr>
                </thead>
                <tbody
                  class="divide-y divide-slate-200 dark:divide-slate-700"
                >
                  <tr
                    v-for="(row, index) in previewRows"
                    :key="index"
                    class="hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <td
                      v-for="column in csvColumns"
                      :key="column"
                      class="px-3 py-2 text-slate-600 dark:text-slate-400 whitespace-nowrap"
                    >
                      {{ row[column] || '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Column Mappings -->
            <div class="space-y-3 mb-6">
              <div
                v-for="column in csvColumns"
                :key="column"
                class="flex items-center gap-3"
              >
                <label
                  class="flex-1 text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[150px] truncate"
                  :title="column"
                >
                  {{ column }}:
                </label>
                <select
                  v-model="columnMappings[column]"
                  class="flex-1 p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm focus:ring-1 focus:ring-woot-500 focus:border-woot-500"
                >
                  <option
                    v-for="field in getAvailableFields(column)"
                    :key="field.value"
                    :value="field.value"
                    :disabled="field.disabled"
                  >
                    {{ field.label }}
                  </option>
                </select>
                <span
                  v-if="columnMappings[column]"
                  class="text-xs text-woot-500 whitespace-nowrap"
                >
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                  → {{ getFieldLabel(columnMappings[column]) }}
                </span>
              </div>
            </div>

            <!-- Validation warning -->
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <div
              v-if="!isMappingValid"
              class="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm"
            >
              ⚠️
              {{
                $t('IMPORT_CONTACTS.NAME_REQUIRED', 'O campo')
              }}
              <strong>{{
                $t('IMPORT_CONTACTS.NAME_FIELD', 'Nome')
              }}</strong>
              {{
                $t(
                  'IMPORT_CONTACTS.NAME_REQUIRED_MSG',
                  'é obrigatório. Mapeie pelo menos uma coluna para "Nome".'
                )
              }}
            </div>
            <!-- eslint-enable vue/no-bare-strings-in-template -->

            <div class="flex justify-end gap-2">
              <Button
                label="Voltar"
                variant="ghost"
                color="slate"
                size="sm"
                @click="handleReset"
              />
              <Button
                :label="`Importar ${totalRows} contatos`"
                variant="solid"
                color="blue"
                size="sm"
                :is-loading="isImportingContact"
                :disabled="!isMappingValid || isImportingContact"
                @click="handleImport"
              />
            </div>
          </div>

          <!-- Step 3: Importing -->
          <div v-if="step === 'importing'">
            <div class="text-center py-8">
              <div class="inline-block mb-4">
                <svg
                  class="animate-spin h-8 w-8 text-woot-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              </div>
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <p class="text-sm text-slate-600 dark:text-slate-400 mb-1">
                {{
                  $t(
                    'IMPORT_CONTACTS.SUCCESS',
                    'Importação enviada com sucesso!'
                  )
                }}
              </p>
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <p class="text-xs text-slate-500 dark:text-slate-500">
                {{
                  $t(
                    'IMPORT_CONTACTS.BACKGROUND_MSG',
                    'Os contatos serão processados em segundo plano. Você pode fechar esta janela.'
                  )
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>
